import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/components/common";
import { CustomerAddress, fullAddress } from "@/components/delivery/CustomerAddress";
import { useAuth } from "@/context/AuthContext";
import { addressFromCep } from "@/lib/lookups";

const UFS = "AC AL AP AM BA CE DF ES GO MA MT MS MG PA PB PR PE PI RJ RN RS RO RR SC SP SE TO".split(" ");
const REQ = [["street", "Rua"], ["district", "Bairro"], ["city", "Cidade"], ["state", "Estado"]];
export const addrText = (a = {}) => [fullAddress(a), a.reference && `Ref.: ${a.reference}`].filter(Boolean).join(" - ");

function PlainFields({ a, setA }) {
  const set = (k) => (e) => setA((x) => ({ ...x, [k]: e.target.value }));
  const onCep = async (e) => {
    const v = e.target.value;
    setA((x) => ({ ...x, cep: v }));
    const patch = await addressFromCep(v, {});
    if (patch) setA((x) => ({ ...x, ...patch }));
  };
  const inp = (k, label, extra = {}) => <Field label={label}><Input value={a[k] || ""} onChange={k === "cep" ? onCep : set(k)} className="h-11" data-testid={`order-addr-${k}`} {...extra} /></Field>;
  return (
    <div className="grid grid-cols-3 gap-3" data-testid="order-address-fields">
      <div className="col-span-3">{inp("cep", "CEP", { inputMode: "numeric", maxLength: 9, placeholder: "00000-000", autoFocus: true })}</div>
      <div className="col-span-2">{inp("street", "Rua *")}</div>
      {inp("number", "Número")}
      <div className="col-span-3">{inp("district", "Bairro *")}</div>
      <div className="col-span-3">{inp("complement", "Complemento")}</div>
      <div className="col-span-3">{inp("reference", "Referência")}</div>
      <div className="col-span-2">{inp("city", "Cidade *")}</div>
      <Field label="Estado *">
        <Select value={a.state || ""} onValueChange={(v) => setA((x) => ({ ...x, state: v }))}>
          <SelectTrigger className="h-11" data-testid="order-addr-state"><SelectValue placeholder="UF" /></SelectTrigger>
          <SelectContent>{UFS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
    </div>
  );
}

export function AddressDialog({ open, onClose, value, onSave, mapMode, loc }) {
  const { session } = useAuth();
  const st = session.tenant.settings;
  const end = session.tenant.endereco || {};
  const base = { city: end.cidade || "", state: (end.uf || "").toUpperCase() };
  const [a, setA] = useState({});
  const [draft, setDraft] = useState(loc);
  useEffect(() => {
    if (!open) return;
    setA({ ...base, ...value });
    setDraft({ ...loc, addr: { ...base, ...loc.addr } });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = () => {
    const cur = mapMode ? draft.addr : a;
    const miss = REQ.filter(([k]) => !String(cur[k] || "").trim()).map(([, l]) => l);
    if (miss.length) { toast.error(`Preencha: ${miss.join(", ")}`); return; }
    onSave(mapMode ? draft : a);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={mapMode ? "max-h-[92vh] max-w-2xl overflow-y-auto" : "max-w-xl"} data-testid="order-address-dialog">
        <DialogHeader><DialogTitle>Endereço para entrega</DialogTitle></DialogHeader>
        {mapMode ? <CustomerAddress slug={session.tenant.menu_slug} st={{ ...st, delivery_pin_adjust: true }} value={draft} onChange={setDraft} /> : <PlainFields a={a} setA={setA} />}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} data-testid="order-address-cancel">Cancelar</Button>
          <Button onClick={save} data-testid="order-address-save">Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
